import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  MenuOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { Tag } from "antd";
import { debounce, noop } from "lodash";
import { FormattedMessage } from "react-intl";
import { useHistory, useParams } from "react-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { httpCreators, Tags } from "@ppvrbh/merchant-frontend-core";
import { FOODMENU_REQUEST_ID_LIST } from "../../FoodMenu/redux/foodmenu-constant";
import { foodMenuSagas } from "../../FoodMenu/redux/foodmenu-saga";
import { FoodMenuService } from "../../FoodMenu/redux/foodmenu-service";

import "../stylescss.css";
import {
  RBHBox,
  RBHButton,
  RBHImage,
  RBHText,
  RBHTextInput,
  TagCount,
} from "../../../../shared/components";
import { RBHList } from "../../../../shared/components/rbh-list";
import styleButton from "../../../../shared/components/rbh-button/styles";
import styleInput from "../../../../shared/components/rbh-textinput/styles";
import { textStyles } from "../../../../shared/theme/fonts-ppv";
import {
  MenuInCatType,
  MenuQCStatusEnum,
} from "../../../../shared/models/food-menu";
import { generalCreators } from "../../../../redux/general/general-action";
import {
  DEFAULT_PAGE_SIZE,
  MODAL_TYPE,
} from "../../../../redux/general/general-constant";
import { GENERAL_STATE_KEY, generalStateSelector } from "../../../../redux/general/general-reducer";
import { FormAddEdit } from "../components/FormAddEdit";
import { catCreators } from "../redux/cat-action";
import { CAT_REQUEST_ID_LIST } from "../redux/cat-constant";
import { CAT_STATE_KEY, catStateSelector } from "../redux/cat-reducer";
import { catSagas } from "../redux/cat-saga";
import { CatService } from "../redux/cat-service";
import styles from "./styles";
import { Colors, Images } from "../../../../shared/theme";
import metrics from "../../../../shared/theme/metrics";
import RBHTable2 from "../../../../shared/components/rbh-table-2";
import images from "../../../../shared/theme/images";
import { SortButton } from "../../components/SortButton";
import { UtilHelper } from "../../../../shared/helpers/utility";

type Props = {
  // Placeholder for future props
};

type MenuRow = MenuInCatType["menus"]["items"][number];
type TableMenuRow = MenuRow & { id: number };

export const FoodCategoryDetailScreen: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams<{ foodCategoryId: string }>();
  const foodCategoryId = params.foodCategoryId || "";
  const foodCategory = useSelector(catStateSelector(CAT_STATE_KEY.DETAIL)) as
    | MenuInCatType
    | undefined;
  const originalMenuInCat = useSelector(
    catStateSelector(CAT_STATE_KEY.ORIGINAL_MENU_IN_CAT),
  ) as MenuInCatType | undefined;
  const lang = useSelector(generalStateSelector(GENERAL_STATE_KEY.LOCALE));

  const deleteMenuInCat = (menuId: number) => {
    dispatch(
      httpCreators.request({
        id: FOODMENU_REQUEST_ID_LIST.DELETE_FOOD_MENU,
        sagaHandler: foodMenuSagas.deleteFoodMenuHandler as any,
        callbackParams: {
          resetReload: () => fetchMenus(currentPage, searchText, pageSize),
        },
        service: FoodMenuService.deleteFoodMenus,
        serviceParams: menuId,
      }),
    );
  };

  const dispatchDeleteModal = (menuId: number) => {
    dispatch(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        onOk: () => deleteMenuInCat(menuId),
        type: MODAL_TYPE.DELETE,
        modalTitle: "main.model_delete_menu_title",
        modalContent: "main.model_delete_menu_content",
      }),
    );
  };

  const columns: ColumnsType<TableMenuRow> = [
    {
      width: metrics.tableHeader.width50,
      title: <FormattedMessage id="main.cat_category.detail.column.picture" />,
      dataIndex: "menuPhoto",
      render: (value: string | undefined, all: MenuRow, index: number) => {
        const menuPicture = "menuPicture-" + index;
        return (
          <RBHBox
            style={{ color: Colors.font.primary.fakeBlack }}
            data-testid={menuPicture}
            id={menuPicture}
          >
            <RBHImage
              src={value || Images.menuTableDefault}
              alt={all?.menuName || "menu-picture"}
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
                borderRadius: 8,
              }}
              onError={(e: any) => (e.target.src = Images.menuTableDefault)}
            />
          </RBHBox>
        );
      },
    },
    {
      width: metrics.tableHeader.width280,
      title: (
        <FormattedMessage id="main.cat_category.detail.column.menu_name" />
      ),
      render: (_value: unknown, record: MenuRow) => {
        return (
          <RBHBox
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              maxWidth: metrics.tableHeader.width280,
            }}
          >
            {/* Name and NameEN */}
            <RBHBox
              style={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <RBHText
                style={{
                  ...textStyles.body2,
                  color: Colors.font.primary.fakeBlack,
                }}
              >
                {record?.menuName || "-"}
              </RBHText>
              <RBHText
                style={{
                  ...textStyles.body2,
                  color: Colors.font.primary.fakeBlack,
                }}
              >
                {record?.menuNameEN || "-"}
              </RBHText>
            </RBHBox>
            {/* SKU ID and Tag */}
            <RBHBox style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <RBHText
                style={{
                  ...textStyles.subtitle3,
                  color: Colors.font.primary.darkGray,
                }}
              >
                <FormattedMessage id="main.cat_category.detail.sku_id" />:{" "}
                {record?.menuSKUId || "-"}
              </RBHText>
              {record?.tagId ? (
                <RBHText
                  style={{
                    ...textStyles.subtitle3,
                    color: Colors.font.primary.darkGray,
                  }}
                >
                  <FormattedMessage id="main.cat_category.detail.tag" />:{" "}
                  {record.tagId}
                </RBHText>
              ) : null}
            </RBHBox>
          </RBHBox>
        );
      },
    },
    {
      width: metrics.tableHeader.width200,
      title: (
        <FormattedMessage id="main.cat_category.detail.column.description" />
      ),
      dataIndex: "menuDescription",
      render: (value: string, record: MenuRow) => {
        return (
          <RBHBox
            style={{
              maxWidth: metrics.tableHeader.width200,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <RBHText
              style={{
                ...textStyles.subtitle3,
                color: Colors.font.primary.darkGray,
              }}
            >
              <FormattedMessage id="main.cat_category.detail.label.description" />
              : {value || "-"}
            </RBHText>
            <RBHText
              style={{
                ...textStyles.subtitle3,
                color: Colors.font.primary.darkGray,
              }}
            >
              <FormattedMessage id="main.cat_category.detail.label.description_en" />
              : {record?.menuDescriptionEN || "-"}
            </RBHText>
          </RBHBox>
        );
      },
    },
    {
      width: metrics.tableHeader.width160,
      title: <FormattedMessage id="main.cat_category.detail.column.price" />,
      dataIndex: "menuPrice",
      render: (value: number | undefined, record: MenuRow) => {
        return (
          <RBHBox
            style={{
              maxWidth: metrics.tableHeader.width160,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <RBHText
              style={{
                ...textStyles.body2,
                fontWeight: 700,
                color: Colors.font.primary.fakeBlack,
              }}
            >
              {value !== undefined && value !== null
                ? "฿ " + value.toLocaleString()
                : "-"}
            </RBHText>
            {record?.menuDiscounts?.map((discount) => (
              <RBHBox
                key={discount.menuDiscountId}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "start",
                  gap: 4,
                }}
              >
                <img src={images.promotionTag} />
                <RBHText
                  style={{
                    ...textStyles.subtitle3,
                    color: Colors.scbPurple,
                  }}
                >
                  {"฿" + discount.priceDiscount.toLocaleString() + " "}
                  <FormattedMessage id="main.cat_category.detail.discount_start" />
                  {" " + UtilHelper.formatFullDate(discount.startDate, lang)}
                </RBHText>
              </RBHBox>
            ))}
          </RBHBox>
        );
      },
    },
    {
      width: metrics.tableHeader.width80,
      title: (
        <FormattedMessage id="main.cat_category.detail.column.menu_status" />
      ),
      dataIndex: "menuQCStatus",
      align: "center" as const,
      render: (value: string | undefined) => {
        if (value === MenuQCStatusEnum.APPROVED) {
          return (
            <Tag color="success" style={{ borderRadius: 12, fontWeight: 500 }}>
              <FormattedMessage id="main.cat_category.detail.status.approved" />
            </Tag>
          );
        }
        if (value === MenuQCStatusEnum.REJECTED) {
          return (
            <Tag color="error" style={{ borderRadius: 12 }}>
              <FormattedMessage id="main.cat_category.detail.status.rejected" />
            </Tag>
          );
        }
        return (
          <Tag color="default" style={{ borderRadius: 12 }}>
            <FormattedMessage id="main.cat_category.detail.pending" />
          </Tag>
        );
      },
    },
    {
      width: metrics.tableHeader.width80,
      title: (
        <FormattedMessage id="main.cat_category.detail.column.visibility" />
      ),
      dataIndex: "menuStatus",
      align: "center" as const,
      render: (value: string) => {
        return value !== 'MN01' ? (
          <Tag
            style={{
              borderRadius: 12,
              backgroundColor: Colors.grey100,
              border: `1px solid ${Colors.grey100}`,
            }}
          >
            <img
              src={images.eyeClose}
              style={{
                width: 14,
                height: 14,
                marginRight: 4,
                verticalAlign: "middle",
              }}
            />
            <FormattedMessage id="main.cat_category.detail.visibility.hide" />
          </Tag>
        ) : (
          <Tag
            style={{
              borderRadius: 12,
              backgroundColor: Colors.white,
              border: `1px solid ${Colors.grey100}`,
            }}
          >
            <img
              src={images.eye}
              style={{
                width: 14,
                height: 14,
                marginRight: 4,
                verticalAlign: "middle",
              }}
            />
            <FormattedMessage id="main.cat_category.detail.visibility.show" />
          </Tag>
        );
      },
    },
    {
      width: metrics.tableHeader.width100,
      title: <FormattedMessage id="main.cat_category.detail.column.action" />,
      align: "center" as const,
      render: (_value: unknown, all: TableMenuRow) => {
        return (
          <RBHBox style={styles.ActionContainerStyle}>
            <RBHBox
              style={{ cursor: "pointer" }}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                e.preventDefault();
                dispatch(
                  catCreators.callFoodCategory(
                    noop,
                    false,
                    (tagMaster: Tags[]) => {
                      const tag = tagMaster.find((t) => t.tagId === all.tagId);
                      const menuCategory = {
                        menuCategoryId: foodCategory?.menuCategoryId,
                        menuCategoryTypeId: foodCategory?.menuCategoryTypeId,
                        menuCategoryName: foodCategory?.menuCategoryName,
                        menuCategoryNameEN: foodCategory?.menuCategoryNameEN,
                        menuCategoryStatus: foodCategory?.menuCategoryStatus,
                      };
                      const editMenu = tag?.isDeleted
                        ? { ...all, menuCategory }
                        : { ...all, tag, menuCategory };
                      history.push("/main/foodmenu", { editMenu });
                    },
                  ),
                );
              }}
            >
              <EditOutlined translate={null} style={{ fontSize: 16 }} />
            </RBHBox>
            <RBHBox
              style={{ cursor: "pointer" }}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                e.preventDefault();
                dispatchDeleteModal(all.menuId);
              }}
            >
              <DeleteOutlined translate={null} style={{ fontSize: 16 }} />
            </RBHBox>
          </RBHBox>
        );
      },
    },
  ];
  const [menuData, setMenuData] = useState<TableMenuRow[]>([]);
  const [totalMenu, setTotalMenu] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const sortedMenuIdsRef = useRef<number[]>([]);

  useEffect(() => {
    setMenuData(
      (foodCategory?.menus.items || []).map((menu) => ({
        ...menu,
        id: menu.menuId,
      })),
    );
    if (foodCategory?.menus?.meta) {
      setCurrentPage(foodCategory.menus.meta.currentPage);
      setPageSize(foodCategory.menus.meta.itemsPerPage);
      if (searchText === '') {
        setTotalMenu(foodCategory.menus.meta.itemCount)
      }
    }
  }, [foodCategory]);

  const fetchMenus = useCallback(
    (page: number, search: string, limit: number = 10) => {
      if (!foodCategoryId) return;
      dispatch(
        catCreators.callMenuInCat(noop, foodCategoryId, undefined, {
          search: search || undefined,
          page,
          limit,
        }),
      );
    },
    [dispatch, foodCategoryId],
  );

  const handleTableChange = (pagination: any) => {
    const page = pagination?.current ?? 1;
    const limit = pagination?.pageSize ?? 10;
    setCurrentPage(page);
    setPageSize(limit);
    fetchMenus(page, searchText, limit);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchMenus(1, "", 10);
  }, [foodCategoryId]);

  const debouncedFetchMenus = useMemo(
    () =>
      debounce((search: string, limit: number) => {
        setCurrentPage(1);
        fetchMenus(1, search, limit);
      }, 1000),
    [fetchMenus],
  );

  useEffect(() => {
    return () => {
      debouncedFetchMenus.cancel();
    };
  }, [debouncedFetchMenus]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      debouncedFetchMenus.cancel();
      setCurrentPage(1);
      fetchMenus(1, searchText, pageSize);
    }
  };

  const onDelete = () => {
    dispatch(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        modalTitle: "main.cat_category_delete_header",
        modalContent: "main.cat_category_delete_content",
        type: MODAL_TYPE.DELETE,
        onOk: () => {
          dispatch(
            httpCreators.request({
              id: CAT_REQUEST_ID_LIST.DELETE_CAT,
              sagaHandler: catSagas.deleteCatHandler as any,
              callbackParams: () => history.push("/main/foodcategory"),
              service: CatService.deleteCat,
              serviceParams: foodCategory?.menuCategoryId,
            }),
          );
        },
      }),
    );
  };

  const openSortFormModal = () => {
    const originalItems = (originalMenuInCat?.menus?.items || []).map(
      (menu) => ({ ...menu, id: menu.menuId }),
    );
    sortedMenuIdsRef.current = originalItems.map((m) => m.menuId);
    dispatch(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        type: MODAL_TYPE.LIST_SORT_FORM,
        modalTitle: "main.cat_category.sort_modal.title",
        modalContent: (
          <RBHList
            dataSource={originalItems.map((m) => ({
              id: m.menuId,
              name: m.menuName,
              nameEN: m.menuNameEN,
            }))}
            renderItem={(item: any) => (
              <RBHText
                style={{
                  ...textStyles.subtitle2,
                  color: Colors.font.primary.fakeBlack,
                }}
              >
                {item.name}
                {item.nameEN ? `(${item.nameEN})` : ""}
              </RBHText>
            )}
            dragable
            onSort={(newItems: any[]) => {
              sortedMenuIdsRef.current = newItems.map((i) => i.id);
            }}
          />
        ),
        onCancel: () => {
          dispatch(
            generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
              visible: false,
            }),
          );
        },
        onOk: () => {
          dispatch(
            generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
              visible: false,
            }),
          );
          dispatch(
            catCreators.sortMenuSequence(
              Number(foodCategory?.menuCategoryId),
              sortedMenuIdsRef.current,
              () => {
                fetchMenus(currentPage, searchText, pageSize);
              },
            ),
          );
        },
      }),
    );
  };

  const onEditSuccess = () => {
    setIsEditing(false);
    fetchMenus(currentPage, searchText, pageSize);
  };

  const onEditCancel = () => {
    dispatch(
      generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
        visible: true,
        onOk: () => setIsEditing(false),
        type: MODAL_TYPE.CONFIRM_EDIT,
      }),
    );
  };

  useEffect(() => {
    return () => {
      setTotalMenu(0)
    }
  }, [foodCategoryId])

  return (
    <RBHBox>
      <RBHText
        style={{
          ...textStyles.h3,
          color: Colors.font.primary.fakeBlack,
          margin: "0 0 24px 0",
        }}
        text="main.cat_category.detail.title"
      />
      <RBHBox
        style={{
          shadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
          padding: "16px 24px",
          backgroundColor: "#fff",
          borderRadius: 16,
          width: "100%",
        }}
      >
        {isEditing ? (
          <FormAddEdit
            editData={foodCategory}
            onSuccessCallback={onEditSuccess}
            onCancel={onEditCancel}
            onNotificationError={noop}
          />
        ) : (
          <RBHBox
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr auto",
              alignItems: "center",
              gap: 16,
            }}
          >
            <RBHBox style={{ display: "flex", flexDirection: "column" }}>
              <RBHText
                style={{
                  ...textStyles.body2,
                  color: Colors.font.primary.darkGray,
                }}
                text="main.cat_category.detail.category_name_th"
              />
              <RBHText
                style={{
                  ...textStyles.body2,
                  color: Colors.font.primary.fakeBlack,
                }}
              >
                {foodCategory?.menuCategoryName}
              </RBHText>
            </RBHBox>

            <RBHBox style={{ display: "flex", flexDirection: "column" }}>
              <RBHText
                style={{
                  ...textStyles.body2,
                  color: Colors.font.primary.darkGray,
                }}
                text="main.cat_category.detail.category_name_en"
              />
              <RBHText
                style={{
                  ...textStyles.body2,
                  color: Colors.font.primary.fakeBlack,
                }}
              >
                {foodCategory?.menuCategoryNameEN || "-"}
              </RBHText>
            </RBHBox>

            <RBHBox style={{ display: "flex", gap: 12 }}>
              <RBHButton
                style={{ border: 0, shadow: "" }}
                onClick={() => setIsEditing(true)}
              >
                <EditOutlined translate={null} />
              </RBHButton>
              <RBHButton style={{ border: 0, shadow: "" }} onClick={onDelete}>
                <DeleteOutlined translate={null} />
              </RBHButton>
            </RBHBox>
          </RBHBox>
        )}
      </RBHBox>
      <RBHBox
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "24px 0 12px 0",
        }}
      >
        <RBHBox style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <RBHText
            style={{ ...textStyles.h3, color: Colors.font.primary.fakeBlack }}
          >
            <FormattedMessage id="main.cat_category.detail.menu_total" />
          </RBHText>
          <TagCount
            id="cat_menu_total_count"
            count={totalMenu || 0}
            size="lg"
          />
          <RBHTextInput
            style={styleInput.inputTableSearch}
            placeholder="main.cat_category.detail.search.placeholder"
            value={searchText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              setSearchText(value);
              debouncedFetchMenus(value, pageSize);
            }}
            onKeyDown={handleSearchKeyDown}
            prefix={<SearchOutlined translate={null} />}
          />
        </RBHBox>
        <RBHBox style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <RBHBox
            styles={{
              ...styleButton.buttonDefault,
              cursor: "pointer",
              padding: "6px 12px",
            }}
            onClick={() => setSearchText("")}
          >
            <SortButton
              disable={(originalMenuInCat?.menus?.meta?.totalItems ?? 0) < 2}
              onClick={openSortFormModal}
              textId="main.cat_category.detail.sort_menu"
            />
          </RBHBox>
          <RBHButton
            id={"cat_addmenu"}
            style={styles.buttonAdd}
            text={"main.cat_addmenu"}
            type="primary"
            icon={<PlusOutlined translate={null} />}
            onClick={() =>
              history.push("/main/addmenu", {
                menuCategory: {
                  menuCategoryId: foodCategory?.menuCategoryId,
                  menuCategoryTypeId: foodCategory?.menuCategoryTypeId,
                  menuCategoryName: foodCategory?.menuCategoryName,
                  menuCategoryNameEN: foodCategory?.menuCategoryNameEN,
                  menuCategoryStatus: foodCategory?.menuCategoryStatus,
                },
              })
            }
          />
        </RBHBox>
      </RBHBox>
      {/* Menus */}
      <RBHBox>
        <RBHTable2
          rowKey="menuId"
          columns={columns}
          dataSource={menuData}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: foodCategory?.menus?.meta?.totalItems ?? 0,
          }}
        />
      </RBHBox>
    </RBHBox>
  );
};
